import {useQuery} from '@tanstack/react-query'
import {ArrowLeft} from 'lucide-react'
import {useNavigate, useParams} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Button} from '@/components/ui/button'
import {AutoAssignRuleRequestCreateWizard} from '@/pages/AutoAssignRuleRequestCreateWizard'
import {RuleRequestStatus} from '@/types/auto-assign-rule-request'
import {PageLayout} from '@/components/shared/PageLayout'
import {queryError} from '@/lib/query-utils'
import {ErrorBanner} from '@/components/ui/error-banner'
import {IconText} from '@/components/shared/IconText'
import {CONCEPT_ICON} from '@/lib/concepts'

export function AutoAssignRuleRequestEdit() {
    const i18n = useI18n()
    const services = useServices()
    usePageTitle(i18n.autoAssignRuleRequests.editPageTitle)
    const navigate = useNavigate()
    const {id} = useParams<{ id: string }>()

    // Fetch existing request
    const {data: request, isLoading, error} = useQuery({
        queryKey: ['rule-request', id],
        queryFn: () => services.autoAssignRules.detailRequest(id!),
        enabled: !!id,
    })

    return (
        <PageLayout
            variant="detail"
            icon={CONCEPT_ICON.autoAssignRuleRequest}
            title={i18n.autoAssignRuleRequests.editPageTitle}
            backTo={true}
            loading={isLoading}
            error={queryError(isLoading, request, error)}
            data-testid="auto-assign-rule-request-edit-page"
        >
            {request && (
                request.status !== RuleRequestStatus.DRAFT ? (
                    <>
                        <ErrorBanner error={new Error(i18n.autoAssignRuleRequests.cannotEdit({status: request.status}))}/>
                        <Button variant="ghost" className="mt-4" onClick={() => navigate(-1)} data-testid="btn-back-not-editable">
                            <IconText icon={ArrowLeft}>{i18n.common.back}</IconText>
                        </Button>
                    </>
                ) : (
                    <AutoAssignRuleRequestCreateWizard
                        mode="edit"
                        requestId={id}
                        initialData={{
                            name: request.name,
                            description: request.description || undefined,
                            conditionExpr: request.conditionExpr,
                            priority: request.priority,
                            removalGraceDays: request.removalGraceDays,
                            owners: request.owners.map(o => ({
                                ownerPartyId: o.ownerPartyId,
                                ownerPartyDisplayName: o.ownerPartyDisplayName,
                                responsibility: o.responsibility,
                            })),
                            userRisk: request.userRisk,
                            profileIds: request.profileIds,
                            version: request.version,
                        }}
                    />
                )
            )}
        </PageLayout>
    )
}
